import type { GeoJSON } from 'geojson'

export interface MapboxDirectionsOptions {
  coordinates: [number, number][] // [lng, lat] pairs
  profile?: 'driving' | 'walking' | 'cycling'
  alternatives?: boolean
  geometries?: 'geojson' | 'polyline' | 'polyline6'
  overview?: 'full' | 'simplified' | 'false'
  accessToken: string
  signal?: AbortSignal
}

export interface MapboxDirectionsResponse {
  routes: Array<{
    geometry: GeoJSON.LineString
    distance: number // meters
    duration: number // seconds
    weight: number
  }>
  waypoints: Array<{
    location: [number, number]
    name: string
  }>
  code?: string
  message?: string
}

export interface MapboxDirectionsError {
  code: string
  message: string
  statusCode?: number
}

/**
 * Get directions from Mapbox Directions API v5
 * 
 * @param options - Directions request options
 * @returns Promise resolving to Mapbox Directions response
 * @throws MapboxDirectionsError on API errors
 */
export async function getDirections(
  options: MapboxDirectionsOptions
): Promise<MapboxDirectionsResponse> {
  const {
    coordinates,
    profile = 'driving',
    alternatives = false,
    geometries = 'geojson',
    overview = 'full',
    accessToken,
    signal
  } = options

  // Validate inputs
  if (!accessToken) {
    throw new Error('Mapbox access token is required')
  }

  if (!coordinates || coordinates.length < 2) {
    throw new Error('At least 2 coordinates are required for directions')
  }

  // Format coordinates as semicolon-separated string: lng1,lat1;lng2,lat2
  const coordinatesString = coordinates
    .map(([lng, lat]) => `${lng},${lat}`)
    .join(';')

  // Build URL
  const baseUrl = 'https://api.mapbox.com/directions/v5/mapbox'
  const url = `${baseUrl}/${profile}/${coordinatesString}`

  // Build query parameters
  const params = new URLSearchParams({
    access_token: accessToken,
    geometries,
    overview
  })

  if (alternatives) {
    params.append('alternatives', 'true')
  }

  const fullUrl = `${url}?${params.toString()}`

  try {
    const response = await fetch(fullUrl, {
      signal,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })

    // Handle HTTP errors
    if (!response.ok) {
      let errorMessage = `Mapbox Directions API error: ${response.status} ${response.statusText}`
      
      try {
        const errorData = await response.json()
        if (errorData.message) {
          errorMessage = errorData.message
        }
      } catch {
        // If we can't parse error JSON, use default message
      }

      // Handle rate limiting
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After')
        const error: MapboxDirectionsError = {
          code: 'RATE_LIMIT_EXCEEDED',
          message: `Rate limit exceeded. ${retryAfter ? `Retry after ${retryAfter} seconds.` : 'Please try again later.'}`,
          statusCode: 429
        }
        throw error
      }

      // Handle other HTTP errors
      const error: MapboxDirectionsError = {
        code: `HTTP_${response.status}`,
        message: errorMessage,
        statusCode: response.status
      }
      throw error
    }

    const data = await response.json()

    // Handle API-level errors (e.g., no route found)
    if (data.code && data.code !== 'Ok') {
      const error: MapboxDirectionsError = {
        code: data.code,
        message: data.message || 'Unknown Mapbox API error',
        statusCode: response.status
      }
      throw error
    }

    // Validate response structure
    if (!data.routes || !Array.isArray(data.routes) || data.routes.length === 0) {
      throw new Error('No routes returned from Mapbox Directions API')
    }

    return data as MapboxDirectionsResponse
  } catch (error) {
    // Handle AbortError (request cancellation)
    if (error instanceof Error && error.name === 'AbortError') {
      throw error
    }

    // Re-throw MapboxDirectionsError as-is
    if (error && typeof error === 'object' && 'code' in error) {
      throw error
    }

    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      const networkError: MapboxDirectionsError = {
        code: 'NETWORK_ERROR',
        message: 'Network error. Please check your connection and try again.'
      }
      throw networkError
    }

    // Handle other errors
    const unknownError: MapboxDirectionsError = {
      code: 'UNKNOWN_ERROR',
      message: error instanceof Error ? error.message : 'Unknown error occurred'
    }
    throw unknownError
  }
}

